// ─── Gestão de Documentos — formatters ───────────────────────────────────────

export function formatData(raw: string | undefined): string {
  if (!raw) return '—';
  const [datePart] = raw.split(' ');
  const parts = datePart.split('/');
  if (parts.length !== 3) return raw;
  const [mm, dd, yyyy] = parts;
  return `${dd}/${mm}/${yyyy}`;
}

export function hoje(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function primeiroDiaMes(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

// ─── Gestão de Documentos — date utils ───────────────────────────────────────

export function sanitizeFilename(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_');
}

export function buildS3Key(cnpj: string, filename: string): string {
  const sanitized = sanitizeFilename(filename).replace(/ /g, '_');
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  return `segregacao-turbopro/${cnpj}/${timestamp}_${sanitized}`;
}

export function formatMoeda(valor: string | undefined): string {
  if (!valor) return '—';
  const num = parseFloat(valor);
  if (isNaN(num)) return valor;
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function formatPct(valor: string | undefined): string {
  if (!valor) return '—';
  const num = parseFloat(valor);
  if (isNaN(num)) return valor;
  return `${num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

export function formatDataHora(raw: string | null | undefined): string {
  if (!raw) return '—';
  const [data, hora] = raw.trim().split(' ');
  if (!data) return raw;
  const partes = data.split('/');
  if (partes.length === 3) {
    return `${partes[0]}/${partes[1]}/${partes[2]}${hora ? ', ' + hora : ''}`;
  }
  return raw;
}

export function formatDataHoraHistorico(raw: string): string {
  if (!raw) return '—';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

export function fmt(v: number | null | undefined, decimais = 2): string {
  if (v == null || isNaN(Number(v))) return '—';
  return Number(v).toLocaleString('pt-BR', {
    minimumFractionDigits: decimais,
    maximumFractionDigits: decimais,
  });
}

export function fmtMoeda(v: number | null | undefined): string {
  if (v == null || isNaN(Number(v))) return '—';
  return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// ─── General ─────────────────────────────────────────────────────────────────

export const formatCurrency = (value: number): string => {
  return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatPercent = (value: number): string => {
  return `${(value * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
};

export const formatNumber = (value: number, decimals = 2): string => {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export const formatQuantidade = (value: number): string => {
  return value.toLocaleString('pt-BR');
};

export const formatCNPJ = (cnpj?: string | null): string => {
  if (!cnpj) return '';
  const raw = String(cnpj);
  const cleaned = raw.replace(/\D/g, '');
  if (cleaned.length !== 14) return raw;
  return cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

function parseDateValue(date: Date | string): Date | null {
  if (date instanceof Date) {
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const normalized = date.trim();
  if (!normalized) {
    return null;
  }

  const brDateTimeMatch = normalized.match(
    /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/
  );

  if (brDateTimeMatch) {
    const [, day, month, year, hours = '00', minutes = '00', seconds = '00'] = brDateTimeMatch;
    const parsed = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hours),
      Number(minutes),
      Number(seconds)
    );

    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export const formatDate = (date?: Date | string | null): string => {
  if (!date) return '';
  const d = parseDateValue(date);
  if (!d) return '';
  return d.toLocaleDateString('pt-BR');
};

export function formatYmdLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getPeriodoUltimosNDias(dias = 30): { dataInicio: string; dataFim: string } {
  const end = new Date();
  const start = new Date();

  start.setDate(start.getDate() - dias);

  return {
    dataInicio: formatYmdLocal(start),
    dataFim: formatYmdLocal(end),
  };
}

export const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
] as const;

export interface PeriodoFiltro {
  modo: 'periodo' | 'mesano';
  mes?: string;
  ano?: number;
  dataInicio?: string;
  dataFim?: string;
  referente?: string;
}

export const formatarValorTabela = (
  valor: unknown,
  tipo: 'moeda' | 'percentual' | 'numero' | 'texto' = 'texto'
): string => {
  if (valor === null || valor === undefined || valor === '') return '';

  switch (tipo) {
    case 'moeda':
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(Number(valor));
    case 'percentual':
      return `${Number(valor).toFixed(4)}%`;
    case 'numero':
      return Number(valor).toFixed(4);
    default:
      return String(valor);
  }
};

export function converterPeriodoParaDatas(filtro: Partial<PeriodoFiltro>): {
  dataInicio: string;
  dataFim: string;
} {
  if (filtro.modo === 'periodo') {
    return {
      dataInicio: filtro.dataInicio || '',
      dataFim: filtro.dataFim || '',
    };
  }

  if (filtro.modo === 'mesano' && filtro.mes && filtro.ano) {
    const mesIndex = (MESES as readonly string[]).indexOf(filtro.mes);

    if (mesIndex !== -1) {
      const ano = filtro.ano;
      const mesNum = String(mesIndex + 1).padStart(2, '0');
      const ultimoDia = new Date(ano, mesIndex + 1, 0).getDate();

      return {
        dataInicio: `${ano}-${mesNum}-01`,
        dataFim: `${ano}-${mesNum}-${String(ultimoDia).padStart(2, '0')}`,
      };
    }
  }

  return { dataInicio: '', dataFim: '' };
}

/** "dd/mm/aaaa" de uma data "aaaa-mm-dd", sem passar por `Date` (que mudaria o dia pelo fuso). */
function diaMesAno(ymd: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
}

/**
 * O texto do botão de período do cabeçalho (SPEC-017, decisão 13): "11/2026" no modo
 * Mês/Ano, as duas datas no modo período, e "Selecionar Período" só quando não há o que mostrar.
 */
export function rotuloDoPeriodo(periodo: {
  periodoModo: 'mesano' | 'periodo';
  periodoMes: string | null;
  periodoAno: number | null;
  dataInicio: string | null;
  dataFim: string | null;
}): string {
  if (periodo.periodoModo === 'mesano') {
    const indice = (MESES as readonly string[]).indexOf(periodo.periodoMes ?? '');
    if (indice !== -1 && periodo.periodoAno !== null) {
      return `${String(indice + 1).padStart(2, '0')}/${periodo.periodoAno}`;
    }
    return 'Selecionar Período';
  }

  const inicio = periodo.dataInicio ? diaMesAno(periodo.dataInicio) : null;
  const fim = periodo.dataFim ? diaMesAno(periodo.dataFim) : null;
  return inicio && fim ? `${inicio} a ${fim}` : 'Selecionar Período';
}
